<script setup lang="ts">
import { computed, markRaw } from 'vue';
import { Download } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { LazyTooltip } from '@/components/ui/tooltip';
import TrackListColumnSettingsContent from '@/components/TrackListColumnSettingsContent.vue';
import { useTrackListColumns } from '@/composables/trackListColumns';
import { storedSearchExportColumns } from '@/composables/searchExportColumns';
import { usePermissions } from '@/composables/usePermissions';
import { useInspectorStore } from '@/stores/inspectorStore';
import type { TrackListExportSource } from '@/types/trackListExport';

/**
 * The Export button for one list: a track list, or a list page's result set
 * for any record type.
 *
 * Pressing it opens the export column list in the inspector, filled in with
 * the person's last export choice. The export itself starts from that list's
 * own Export button. See TrackListColumnSettingsContent.
 *
 * Renders nothing without the `list_export` permission. The endpoint refuses
 * on its own. This only keeps a button the person cannot use off the screen.
 */
const props = withDefaults(
  defineProps<{
    source: TrackListExportSource;
    /** The list's name for the toast and the email ("Bucket - 90s Alt Rock", "Account search"). */
    label: string;
    /**
     * A list page's columns on screen, in their order. The column list starts
     * from these until the person has exported this record type once. Leave
     * it out on a track list, which has its own fixed set of columns.
     */
    columns?: string[] | null;
    /**
     * The columns a list page can export, with their names. Leave it out on a
     * track list.
     */
    columnOptions?: ReadonlyArray<{ id: string; label: string }> | null;
    /**
     * How much room the host toolbar has. `full` is a labelled button, `icons`
     * moves the word into a tooltip, and `collapsed` renders a menu item for a
     * menu the host owns.
     */
    fit?: 'full' | 'icons' | 'collapsed';
    /**
     * Button styling, to match the host toolbar. The track lists' toolbars use
     * `outline`. The list page's toolbar buttons are all `toolbar`.
     */
    buttonVariant?: 'outline' | 'toolbar';
  }>(),
  { columns: null, columnOptions: null, fit: 'full', buttonVariant: 'outline' },
);

const { can } = usePermissions();
const inspectorStore = useInspectorStore();
const trackListColumnIds = useTrackListColumns();

const canExport = computed(() => can('view', 'list_export'));

const BUTTON_WORD = 'Export';
const TOOLTIP_TEXT = 'Export to Excel';

/**
 * What the column list offers on a list page. The page's own columns, plus
 * any column on screen that the page did not name, so every ticked column can
 * also be unticked.
 */
function listPageColumnOptions(screenColumnIds: string[]): Array<{ id: string; label: string }> {
  const options = [...(props.columnOptions ?? [])];
  const namedIds = new Set(options.map((column) => column.id));
  for (const columnId of screenColumnIds) {
    if (!namedIds.has(columnId)) {
      options.push({ id: columnId, label: columnId });
      namedIds.add(columnId);
    }
  }
  return options;
}

function openColumnList(): void {
  const listProps: Record<string, unknown> = { source: props.source, label: props.label };

  if (props.source.type === 'search') {
    const screenColumnIds = props.columns ?? [];
    listProps.initialColumnIds = storedSearchExportColumns(props.source.entity) ?? screenColumnIds;
    listProps.columnOptions = listPageColumnOptions(screenColumnIds);
    listProps.resetColumnIds = screenColumnIds;
  } else {
    listProps.initialColumnIds = [...trackListColumnIds.value];
  }

  // A track list already sits in the inspector, so the column list goes on
  // top of it and closing it goes back there. A list page has nothing in the
  // inspector, so the column list opens on its own.
  if (inspectorStore.isOpen) {
    inspectorStore.push(BUTTON_WORD, markRaw(TrackListColumnSettingsContent), listProps);
    return;
  }
  inspectorStore.open(BUTTON_WORD, markRaw(TrackListColumnSettingsContent), listProps);
}
</script>

<template>
  <!--
    `collapsed`: the host has no room for buttons and renders a menu, so this
    is an item in it. Same test id as the button, because it is the same control.
  -->
  <DropdownMenuItem
    v-if="canExport && fit === 'collapsed'"
    data-testid="track-list-export-button"
    @click="openColumnList"
  >
    <Download class="mr-2 size-4" aria-hidden="true" />
    {{ BUTTON_WORD }}
  </DropdownMenuItem>

  <!--
    `full` and `icons`: a button, labelled while there is room. Without the
    word, the name moves to a tooltip and to an aria-label. The aria-label goes
    on only then, because it overrides the visible text, and the two must not
    disagree (WCAG 2.5.3, Label in Name).
  -->
  <div v-else-if="canExport" class="flex items-center gap-2" data-testid="track-list-export-controls">
    <LazyTooltip>
      <Button
        :variant="buttonVariant"
        size="sm"
        class="px-2"
        data-testid="track-list-export-button"
        :aria-label="fit === 'full' ? undefined : TOOLTIP_TEXT"
        @click="openColumnList"
      >
        <Download class="size-3.5 shrink-0" aria-hidden="true" />
        <span v-if="fit === 'full'">{{ BUTTON_WORD }}</span>
      </Button>
      <template #content>{{ TOOLTIP_TEXT }}</template>
    </LazyTooltip>
  </div>
</template>
